import { useEffect, useRef } from 'react';
import type { KeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import { formatNumber } from '@bookingdei/i18n/format-number';
import { useLanguage } from './language-context';
import { SignOutSpinner, useSigningOut } from './sign-out-progress';
import { useLayer, LayerScrim } from './layer-stack';
import { Icon } from './icon';
import { accountMenuItems } from './nav-items';
import type { ShellBadges } from './nav-items';
import type { User } from '../mock/seeds/schema-types';
import './account-menu.css';
import { formatPhone } from '@bookingdei/i18n/phone';
import { phonePlan } from '../theme/phone-plan';
import { Avatar } from './avatar';
import { roleSwitchLinks } from './role-switch-links';

// Account menu (CB-17; Others.dc.html L253-286): who is signed in, then Account ·
// My bookings · Saved · Help, plus Messages and Claims (DR-38 keeps them; on desktop
// this menu is their home, since the rail has no More), then the other dashboards this
// account can open (role-switch-links.ts — the 30 Oct 2026 amendment to DR-38), and Sign
// out. No session list (DR-38). The popover is
// on the layer stack: Esc closes it and focus returns to the avatar button; arrow keys
// move between items.

export function AccountMenu({
  open,
  onClose,
  user,
  badges,
  onSignOut,
}: {
  open: boolean;
  onClose: () => void;
  user: User | undefined;
  badges: ShellBadges;
  onSignOut: () => void;
}) {
  const { t, lang } = useLanguage();
  const signingOut = useSigningOut();
  const { z } = useLayer(open, onClose, { kind: 'sheet', history: false });
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) menuRef.current?.querySelector<HTMLElement>('[role="menuitem"]')?.focus();
  }, [open]);

  if (!open) return null;

  const switches = roleSwitchLinks(user);
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? []);
    if (items.length === 0) return;
    e.preventDefault();
    const i = items.indexOf(document.activeElement as HTMLElement);
    const next = e.key === 'ArrowDown' ? (i + 1) % items.length : (i - 1 + items.length) % items.length;
    items[next]?.focus();
  };

  return (
    <LayerScrim z={z} onClose={onClose} className="popover-catcher">
      <div
        ref={menuRef}
        className="account-menu"
        role="menu"
        aria-label={t('user.shell.account.menu')}
        onKeyDown={onKeyDown}
        data-motion=""
      >
        <div className="account-menu__head">
          <Avatar user={user} size={34} />
          <span className="account-menu__id">
            <span className="account-menu__name">{user?.name}</span>
            <span className="account-menu__phone">{formatPhone(user?.phone, phonePlan)}</span>
          </span>
        </div>
        <div className="account-menu__items">
          {accountMenuItems.map((item) => {
            const count = item.badge ? badges[item.badge] : 0;
            return (
              <Link key={item.id} to={item.to} role="menuitem" className="account-menu__item" onClick={onClose}>
                <span className="account-menu__icon">
                  <Icon name={item.icon} size={17} stroke={1.7} />
                </span>
                <span className="account-menu__label">{t(item.key)}</span>
                {count > 0 && <span className="account-menu__count">{formatNumber(lang, count)}</span>}
              </Link>
            );
          })}
        </div>
        {switches.length > 0 && (
          <div className="account-menu__items account-menu__switch">
            {switches.map((link) => (
              <a key={link.id} href={link.href} role="menuitem" className="account-menu__item">
                <span className="account-menu__icon">
                  <Icon name={link.icon} size={17} stroke={1.7} />
                </span>
                <span className="account-menu__label">{t(link.key)}</span>
              </a>
            ))}
          </div>
        )}
        <div className="account-menu__foot">
          <button
            type="button"
            role="menuitem"
            className="account-menu__item account-menu__item--danger"
            onClick={onSignOut}
            disabled={signingOut}
            aria-busy={signingOut}
          >
            <span className="account-menu__icon">
              {signingOut ? <SignOutSpinner /> : <Icon name="signOut" size={17} stroke={1.7} />}
            </span>
            <span className="account-menu__label">{t(signingOut ? 'common.signing_out' : 'user.shell.sign_out')}</span>
          </button>
        </div>
      </div>
    </LayerScrim>
  );
}
